/**
 * Klaviyo Back In Stock notification modal and form functionality.
 * Configuration is passed via data attributes on the script tag.
 *
 * @typedef {Object} KlaviyoConfig
 * @property {string} triggerText - The trigger text for the notification button
 * @property {string} popupBodyText - The body text displayed in the popup
 * @property {string} popupBtnText - The submit button text in the popup
 * @property {string} popupSuccessText - The success message after form submission
 */

/**
 * Retrieves a typed element from the DOM.
 * @template {HTMLElement} T
 * @param {string} selector - CSS selector
 * @param {new () => T} [_type] - Optional type hint (unused at runtime)
 * @returns {T | null}
 */
const getElement = (selector, _type) => /** @type {T | null} */(document.querySelector(selector));

/**
 * Retrieves a typed element by ID from the DOM.
 * @template {HTMLElement} T
 * @param {string} id - Element ID
 * @param {new () => T} [_type] - Optional type hint (unused at runtime)
 * @returns {T | null}
 */
const getElementById = (id, _type) => /** @type {T | null} */(document.getElementById(id));

document.addEventListener('DOMContentLoaded', () => {
  // Get configuration from script tag data attributes
  const scriptTag = document.getElementById('klaviyo-back-in-stock-script');
  if (!scriptTag) {
    console.error('Klaviyo back-in-stock script tag not found');
    return;
  }

  /** @type {KlaviyoConfig} */
  const config = {
    triggerText: scriptTag.dataset.triggerText || 'Notify Me When Available',
    popupBodyText: scriptTag.dataset.popupBodyText || 'Enter your email to be notified when this product is back in stock.',
    popupBtnText: scriptTag.dataset.popupBtnText || 'Notify Me When Available',
    popupSuccessText: scriptTag.dataset.popupSuccessText || "Thank you! We'll notify you when this item is back in stock.",
  };

  // Klaviyo BIS integration (if Klaviyo is available)
  if (!window.klaviyo || typeof window.klaviyo.enable !== 'function') {
    console.warn('Klaviyo not ready yet');
    return;
  }

  if (window.klaviyo) {
    window.loadKlaviyoBis = () =>
      window.klaviyo.enable('backinstock', {
        trigger: {
          product_page_text: config.triggerText,
          product_page_class: ' product-details button add-to-cart-button button',
          product_page_text_align: 'center',
          product_page_margin: '0px',
          replace_anchor: true,
        },
        modal: {
          headline: '{product_name}',
          body_content: config.popupBodyText,
          email_field_label: 'Email',
          button_label: config.popupBtnText,
          subscription_success_label: config.popupSuccessText,
          footer_content: '',
          drop_background_color: '#000',
          background_color: '#fff',
          text_color: '#000000',
          button_text_color: '#fff',
          button_background_color: '#000000',
          close_button_color: '#ccc',
          error_background_color: '#fcd6d7',
          error_text_color: '#C72E2F',
          success_background_color: '#d3efcd',
          success_text_color: '#1B9500',
        },
      });
  }

  // DOM Element references
  const klaviyoDynamicDialog = getElement('.dynamic-klaviyo-modal-popup dialog', HTMLDialogElement);
  const klaviyoDynamicCloseButton = document.querySelector('.dynamic-klaviyo-modal-popup dialog .close-button');
  const klaviyoNotifyContainer = getElement('#klaviyo-early-access-modal', HTMLDialogElement);
  const klaviyoNotifyDialog = getElement('#klaviyo-early-access-modal dialog', HTMLDialogElement);
  const klaviyoNotifyCloseButton = document.querySelector('.klaviyo-notify-modal dialog .close-button');
  const klaviyoNotifyForm = getElementById('klaviyo-notify-form', HTMLFormElement);
  const klaviyoListIdInput = getElementById('klaviyo-list-id', HTMLInputElement);

  // Move the function to global scope
  window.signup_for_access = function () {
    if (klaviyoDynamicDialog) {
      klaviyoDynamicDialog.showModal();
    }
  };

  if (klaviyoDynamicCloseButton) {
    klaviyoDynamicCloseButton.addEventListener('click', () => {
      if (klaviyoDynamicDialog) {
        klaviyoDynamicDialog.close();
      }
    });
  }

  // Handle klaviyo notify modal
  if (klaviyoNotifyCloseButton) {
    klaviyoNotifyCloseButton.addEventListener('click', () => {
      if (klaviyoNotifyContainer && klaviyoNotifyDialog) {
        klaviyoNotifyContainer.classList.add('hidden');
        klaviyoNotifyDialog.close();
      }
    });
  }

  // Handle notify me button clicks - get list ID from script tag's data attribute
  const listId = scriptTag.dataset.klaviyoListId;
  const earlyAccessTrigger = getElement('.klaviyo-early-access-trigger', HTMLAnchorElement);

  if (earlyAccessTrigger && listId) {
    earlyAccessTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (klaviyoNotifyContainer && klaviyoNotifyDialog && klaviyoListIdInput) {
        klaviyoListIdInput.value = listId;
        klaviyoNotifyContainer.classList.remove('hidden');
        klaviyoNotifyDialog.showModal();
      }
    });
  }

  // Handle form submission
  if (klaviyoNotifyForm) {
    klaviyoNotifyForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      const emailInput = getElementById('klaviyo-notify-email', HTMLInputElement);
      const listIdInput = getElementById('klaviyo-list-id', HTMLInputElement);

      const email = emailInput?.value || '';
      const listId = listIdInput?.value || '';

      const errorMessage = getElement('.klaviyo_messages .error_message', HTMLElement);
      const successMessage = getElement('.klaviyo_messages .success_message', HTMLElement);

      if (!email || !listId) {
        if (errorMessage) {
          errorMessage.textContent = 'Please enter a valid email address.';
          errorMessage.style.display = 'block';
        }
        if (successMessage) {
          successMessage.style.display = 'none';
        }
        return;
      }

      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=UhZ7WP`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-07-15',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'back_in_stock_pdp',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email,
                    },
                  },
                },
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: listId,
                  },
                },
              },
            },
          }),
        });

        if (response.ok) {
          if (successMessage) {
            successMessage.style.display = 'block';
          }
          if (errorMessage) {
            errorMessage.style.display = 'none';
          }

          // Clear the form and close modal after a delay
          setTimeout(() => {
            klaviyoNotifyForm.reset();
            if (klaviyoNotifyDialog) {
              klaviyoNotifyDialog.close();
            }
            if (successMessage) {
              successMessage.style.display = 'none';
            }
          }, 2000);
        } else {
          throw new Error('Subscription failed');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        if (errorMessage) {
          errorMessage.textContent = 'Something went wrong. Please try again.';
          errorMessage.style.display = 'block';
        }
        if (successMessage) {
          successMessage.style.display = 'none';
        }
      }
    });
  }

  // Use MutationObserver to wait for the Klaviyo form to load
  const observer = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        const klaviyoSignup = document.querySelector('.product__info-container .rte .klaviyo-form');
        if (klaviyoSignup && klaviyoDynamicDialog) {
          klaviyoDynamicDialog.append(klaviyoSignup);
          console.log('Klaviyo form moved into dialog:', klaviyoSignup);
          observer.disconnect(); // Stop observing once the form is found
        }
      }
    }
  });

  // Observe changes in the DOM
  const targetNode = document.querySelector('[class*="__product_description_"]');
  if (targetNode) {
    observer.observe(targetNode, { childList: true, subtree: true });
  } else {
    console.error('Target node for observing not found!');
  }
});
